import {
  AccountEnvironment,
  UserRepresentation,
  getPersonalInfo,
  useEnvironment,
  usePromise
} from "@keycloak/keycloak-account-ui";
import { Button, PageToggleButton } from "@patternfly/react-core";
import { useState } from "react";
import { BarsIcon, TimesIcon } from "@patternfly/react-icons";
import { Link } from "react-router-dom";
import { environment } from "./environment";
import { useTranslation } from "react-i18next";

type HeaderProps = {
  pageToggleButton?: boolean;
  isMenuOpen: boolean;
  setIsMenuOpen: (open:boolean) => void;
};
import { TopRoute } from "./routes";


export const Header: React.FC<HeaderProps> = ({ pageToggleButton = true, isMenuOpen, setIsMenuOpen, }) => {
  // KeycloakProvider を使う
  const context = useEnvironment<AccountEnvironment>();
  const keycloak = context.keycloak;
  const [personalInfo, setPersonalInfo] = useState<UserRepresentation>();
  const rootPath = decodeURIComponent(new URL(environment.baseUrl).pathname);
  const normalizedRootPath = rootPath.endsWith("/") ? rootPath.slice(0, -1) : rootPath;

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const { t } = useTranslation();

  // ログイン状態であればユーザ情報を取得する
  if( keycloak.authenticated === true ){
    usePromise((signal) => getPersonalInfo({ signal, context }), setPersonalInfo);
  }

  return (
    <header className={"pf-v5-c-page__header"} >
      <div className="nc-header-left">
        <div className={"pf-c-page__header-brand-toggle"}>
        { keycloak.authenticated &&  pageToggleButton && (
          <PageToggleButton
            aria-label="ナビゲーションを切り替え"
            aria-expanded={isMenuOpen}
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            {isMenuOpen ? <TimesIcon /> : <BarsIcon />}
          </PageToggleButton>
        )}
        </div>
        <div className="pf-c-page__header-brand">
          <div className="pf-c-page__header-brand-link">
            <div className="nc-common-header_logo-img">
            <Link to={normalizedRootPath} className={keycloak.authenticated ? "logo logged-in" : "logo logged-out"}/>
            </div>
          </div>
        </div>
      </div>

      {/* ログイン状態によってボタンと名前を出し分ける */}
      <div className="nc-header-tools">
          { keycloak.authenticated === true ? (
            <>
              <Button
                id={"signOutButton"}
                onClick={() =>
                  keycloak.logout({
                    redirectUri: TopRoute.account.replace(/\/$/, ""),
                  })
                }
              >
              {t("doSignOut")}
              </Button>
               {/* iPhone 用三点アイコン */}
              <div className="pf-c-dropdown signout-mobile">
                <button
                  id="signOutButton-mobile"
                  className="pf-c-dropdown__toggle pf-m-plain "
                  aria-label="Actions"
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                >
                  <svg
                    fill="currentColor"
                    height="1em"
                    width="1em"
                    viewBox="0 0 192 512"
                    aria-hidden="true"
                    role="img"
                    style={{ verticalAlign: "-0.125em" }}
                  >
                    <path d="M96 184c39.8 0 72 32.2 72 72s-32.2 72-72 72-72-32.2-72-72 32.2-72 72-72zM24 80c0 39.8 32.2 72 72 72s72-32.2 72-72S135.8 8 96 8 24 40.2 24 80zm0 352c0 39.8 32.2 72 72 72s72-32.2 72-72-32.2-72-72-72-72 32.2-72 72z"></path>
                  </svg>
                </button>
                
              </div>
              <span id="nc-header-tools_loggedInUser" className="userName">
                {(personalInfo?.lastName || '') + ' ' + (personalInfo?.firstName || '')} 様
              </span>
              </>
          ) : (
            <>
            <Button
              id={"signInButton"}
              onClick={() => keycloak.login()}
            >
              {t("doSignIn")}
            </Button>
            <div className="pf-c-dropdown signin-mobile">
                <button
                  id="signInButton-mobile"
                  className="pf-c-dropdown__toggle pf-m-plain "
                  aria-label="Actions"
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                >
                <svg
                  fill="currentColor"
                  height="1em"
                  width="1em"
                  viewBox="0 0 192 512"
                  aria-hidden="true"
                  role="img"
                  style={{ verticalAlign: "-0.125em" }}
                >
                  <path d="M96 184c39.8 0 72 32.2 72 72s-32.2 72-72 72-72-32.2-72-72 32.2-72 72-72zM24 80c0 39.8 32.2 72 72 72s72-32.2 72-72S135.8 8 96 8 24 40.2 24 80zm0 352c0 39.8 32.2 72 72 72s72-32.2 72-72-32.2-72-72-72-72 32.2-72 72z"></path>
                </svg>
              </button>
            </div>
            </>
          )}
           <ul
            id="landingMobileDropdown"
            aria-labelledby="landingMobileKebabButton"
            className="pf-v5-c-dropdown__menu pf-m-align-right"
            role="menu"
            style={{ display: isDropdownOpen ? "block" : "none" }}
          >
            <div
              className={
                keycloak.authenticated
                  ? "pf-v5-c-dropdown__menu pf-m-align-logout"
                  : "pf-v5-c-dropdown__menu pf-m-align-login"
              }
              role="menu"
              style={{ display: isDropdownOpen ? "block" : "none" }}
            >
              {keycloak.authenticated ? (
                <li role="none">
                  <button
                    onClick={() =>
                      keycloak.logout({
                        redirectUri: TopRoute.account.replace(/\/$/, ""),
                      })
                    }
                    role="menuitem"
                    tabIndex={0}
                    aria-disabled={false}
                    className="pf-c-dropdown__menu-item"
                  >
                    {t("doSignOut")}
                  </button>
                </li>
              ) : (
                <li role="none">
                  <button
                    onClick={() => keycloak.login()}
                    role="menuitem"
                    tabIndex={0}
                    aria-disabled={false}
                    className="pf-c-dropdown__menu-item"
                  >
                    {t("doSignIn")}
                  </button>
                </li>
              )}
            </div>
          </ul>
        </div>
    </header>
  );
};

